import React, {useEffect} from 'react';
import {useDispatch, useSelector, useStore} from 'react-redux';
import ModalWindow from '../ModalWindow/ModalWindow';
import ModalButton from '../ModalWindow/ModalButton/ModalButton';
import './SupplyRedistributeConfirmModal.css';
import {SUPPLY_COLOR, WAR_COLOR} from '../../../styles/constants';
import {selectShowSupplyRedistributeConfirmModal} from '../../../redux/reducers/privateStateReducer/selectors';
import {hideSupplyRedistributeConfirmModal, resetPendingSupply} from '../../../redux/actions';
import {REGISTRY_SINGLETON} from '../../../scenes/Registry';
import {selectIsParallelSupply} from '../../../redux/reducers/sharedStateReducer/selectors';
import {
  selectIsParallelSupplyPhase,
  selectSupplyActingPlayer
} from '../../../utils/gameState/supply/parallelSupplySelectors';

/**
 * Игрок одновременной фазы снабжения сетевой игры, чью раздачу сбрасывает
 * модалка, или null — если фаза уже закрылась или игрок закончил свою часть
 * (модалка могла провисеть до конца таймера фазы)
 */
const selectParallelSupplyActorStillActing = (state) => (
  selectIsParallelSupplyPhase(state) ? selectSupplyActingPlayer(state) : null
);

/** Модалка одновременной фазы снабжения, которой больше не за кого действовать */
const selectIsParallelSupplyModalStale = (state) => selectIsParallelSupply(state)
  && !selectParallelSupplyActorStillActing(state);

/**
 * Подтверждение сброса всей раздачи снабжения — кнопка «Заново» в панели фазы
 * снабжения (SupplyRedistributeButton). Подтверждение красное: оно снимает всю
 * раздачу, а «Оставить» ничего не меняет. Смонтирована на уровне
 * GameContent, как и прочие модалки (см. SupplyFarmsConfirmModal — иначе
 * рисуется под боковыми панелями).
 */
const SupplyRedistributeConfirmModal = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const showModal = useSelector(selectShowSupplyRedistributeConfirmModal);
  const isParallelSupplyModalStale = useSelector(selectIsParallelSupplyModalStale);

  // Одновременная фаза снабжения: фаза закрылась или часть игрока завершена,
  // пока модалка была открыта, — сбрасывать уже нечего. Закрываем флаг, а не
  // только прячем: иначе модалка всплыла бы снова в снабжении следующего раунда
  useEffect(() => {
    if (showModal && isParallelSupplyModalStale) {
      dispatch(hideSupplyRedistributeConfirmModal());
    }
  }, [showModal, isParallelSupplyModalStale, dispatch]);

  if (!showModal) {
    return null;
  }

  const handleCancel = () => {
    dispatch(hideSupplyRedistributeConfirmModal());
  };

  const handleConfirm = () => {
    dispatch(hideSupplyRedistributeConfirmModal());

    // Состояние — свежее из стора, а не из рендера. Режим — по флагу партии, а
    // не по фазе: если фаза уже закрылась, поочередный сброс без игрока здесь
    // неуместен
    const state = store.getState();
    if (selectIsParallelSupply(state)) {
      // Одновременная фаза снабжения: сбрасывается раздача только СВОЕГО
      // игрока — перепроверив, что фаза еще идет и он свою часть не закончил
      const supplyActor = selectParallelSupplyActorStillActing(state);
      if (supplyActor) {
        dispatch(resetPendingSupply(supplyActor.id));
        REGISTRY_SINGLETON.getSoundManager().playDistributeSupply();
      }
      return;
    }

    dispatch(resetPendingSupply());
    REGISTRY_SINGLETON.getSoundManager().playDistributeSupply();
  };

  return (
    <ModalWindow handleClose={handleCancel} className={'supply-redistribute-confirm-modal'}>
      <div className="supply-redistribute-confirm-modal-content">
        <h2>
          {'Вся раздача '}
          <span style={{color: SUPPLY_COLOR}}>снабжения</span>
          <img
            className="supply-redistribute-confirm-modal-supply-img"
            src={'/img/icons/supply.png'}
            alt={'Снабжение'}
          />
          {' будет отменена.'}
          <br/>И распределить его нужно будет заново.
          <br/>
          {'Уверены?'}
        </h2>
      </div>

      <div className="supply-redistribute-confirm-modal-footer">
        <ModalButton
          onClick={handleConfirm}
          text={'Подтвердить'}
          className={'supply-redistribute-confirm-modal-button'}
          style={{backgroundColor: WAR_COLOR}}
        />
        <ModalButton
          onClick={handleCancel}
          text={'Оставить'}
          className={'supply-redistribute-confirm-modal-button'}
        />
      </div>
    </ModalWindow>
  );
};

export default SupplyRedistributeConfirmModal;
